<section class="site-card tile-flat">
  <header class="tile-flat__header">
    <div class="site-card__head">
      <div class="site-card__head-left">
        <div class="site-card__title">
          <h5 class="site-card__name">{{ moodleSiteTitle() }}</h5>
          <a
            *ngIf="site.moodle_site_url"
            class="site-card__url"
            [href]="site.moodle_site_url"
            target="_blank"
            rel="noopener"
          >{{ site.moodle_site_url }}</a>
        </div>
      </div>
      <div class="site-card__head-right">
        <!-- Status pill — distinguishes paused / online / not-seen so the
             admin can tell at a glance whether the integration is live
             before clicking anything. -->
        <span
          class="status-pill"
          [class.is-online]="!site.disabled_at && !!site.last_seen_at"
          [class.is-paused]="!!site.disabled_at"
        >
          <span class="status-pill__dot"></span>
          <span>
            {{
              site.disabled_at
                ? ('pages.tutors.placements.lms.paused' | translate)
                : site.last_seen_at
                  ? ('pages.tutors.placements.lms.online' | translate)
                  : ('pages.tutors.placements.lms.notSeen' | translate)
            }}
          </span>
        </span>
        <span
          class="site-card__count"
          [class.is-loading]="loading || refreshing"
        >
          <ng-container *ngIf="loading || refreshing; else placementCount">
            <nb-icon icon="sync-outline" class="spin"></nb-icon>
            {{ 'pages.tutors.placements.lms.syncing' | translate }}
          </ng-container>
          <ng-template #placementCount>
            {{ placements.length }} {{ 'pages.tutors.placements.lms.placementsCount' | translate }}
          </ng-template>
        </span>
        <button
          nbButton
          size="small"
          status="primary"
          outline
          class="secondary-action-button"
          [disabled]="refreshing"
          (click)="refresh()"
          [attr.aria-label]="'pages.tutors.placements.lms.refresh' | translate"
        >
          <nb-icon
            [icon]="refreshing ? 'sync-outline' : 'refresh-outline'"
            [class.spin]="refreshing"
          ></nb-icon>
          {{ 'buttons.refresh' | translate }}
        </button>
      </div>
    </div>
  </header>

  <div *ngIf="loading" class="site-card__loading">
      <nb-spinner status="basic"></nb-spinner>
      <span>{{ 'pages.tutors.placements.lms.loadingHint' | translate }}</span>
    </div>

    <div *ngIf="error && !loading" class="site-card__error">
      <nb-icon icon="alert-triangle-outline"></nb-icon>
      <span>{{ error }}</span>
      <button nbButton size="small" status="basic" (click)="refresh()">
        {{ 'pages.tutors.placements.lms.retry' | translate }}
      </button>
    </div>

    <!-- v2.x / unregistered plugin: inline upgrade hint. Non-error
         styling — the Moodle admin just needs to update the block. -->
    <div *ngIf="pluginOutdated && !loading && !error" class="site-card__plugin-outdated">
      <nb-icon icon="info-outline"></nb-icon>
      <div>
        <strong>{{ 'pages.tutors.placements.lms.pluginOutdatedTitle' | translate }}</strong>
        <p>{{ 'pages.tutors.placements.lms.pluginOutdatedHint' | translate }}</p>
      </div>
    </div>

    <!-- Outbound channel degraded: courses + placements come from
         canonical fallback so the UI stays functional. One-line
         banner with a contact-us pointer; full diagnostics live in
         the support channel, not in the customer's face. -->
    <div *ngIf="pluginDegraded && !loading && !error" class="site-card__plugin-degraded">
      <nb-icon icon="alert-circle-outline"></nb-icon>
      <p>
        {{ degradedBannerKey | translate }}
        <a
          href="mailto:support@alphabees.de"
          class="contact-link"
        >{{ 'pages.tutors.placements.lms.healthBanner.contactCta' | translate }}</a>
      </p>
    </div>

    <ng-container *ngIf="!loading && !error">
      <div *ngIf="refreshing" class="site-card__refreshing">
        <nb-icon icon="sync-outline" class="spin"></nb-icon>
        <span>{{ 'pages.tutors.placements.lms.refreshingHint' | translate }}</span>
      </div>

      <!-- Filter -->
      <div class="site-card__filter">
        <nb-form-field>
          <nb-icon nbPrefix icon="search-outline"></nb-icon>
          <input
            type="text"
            nbInput
            fieldSize="small"
            [(ngModel)]="filterInput"
            (keyup.enter)="applyTextFilter()"
            [placeholder]="'pages.tutors.placements.lms.filterPlaceholder' | translate"
          />
          <nb-icon
            *ngIf="filterInput"
            nbSuffix
            icon="close-outline"
            class="site-card__clear-filter"
            (click)="clearTextFilter()"
            [attr.title]="'pages.tutors.placements.lms.clearFilter' | translate"
          ></nb-icon>
        </nb-form-field>
        <nb-select
          [(ngModel)]="categoryFilter"
          (selectedChange)="onCourseFilterChange()"
          size="small"
          [placeholder]="'pages.tutors.placements.lms.filters.category' | translate"
        >
          <nb-option [value]="null">{{ 'pages.tutors.placements.lms.filters.allCategories' | translate }}</nb-option>
          <nb-option *ngFor="let category of categoryOptions" [value]="category.id">
            {{ category.label || category.path || category.name || ('pages.tutors.placements.lms.filters.unnamedCategory' | translate) }}
            <span *ngIf="category.count != null" class="muted">({{ category.count }})</span>
          </nb-option>
        </nb-select>
        <nb-select
          [(ngModel)]="placementStatusFilter"
          (selectedChange)="onCourseFilterChange()"
          size="small"
        >
          <nb-option value="all">{{ 'pages.tutors.placements.lms.filters.anyPlacement' | translate }}</nb-option>
          <nb-option value="placed">{{ 'pages.tutors.placements.lms.filters.placed' | translate }}</nb-option>
          <nb-option value="unplaced">{{ 'pages.tutors.placements.lms.filters.unplaced' | translate }}</nb-option>
        </nb-select>
        <nb-select
          [(ngModel)]="courseSyncStatusFilter"
          (selectedChange)="onCourseFilterChange()"
          size="small"
          [disabled]="autoCourseSyncCoversAll"
          [nbTooltip]="'pages.tutors.placements.lms.siteAutoSyncFilterTooltip' | translate"
          [nbTooltipDisabled]="!autoCourseSyncCoversAll"
          nbTooltipPlacement="top"
        >
          <nb-option value="all">{{ 'pages.tutors.placements.lms.filters.anySync' | translate }}</nb-option>
          <nb-option value="enabled">{{ 'pages.tutors.placements.lms.filters.syncEnabled' | translate }}</nb-option>
          <nb-option value="disabled">{{ 'pages.tutors.placements.lms.filters.syncDisabled' | translate }}</nb-option>
        </nb-select>
        <app-pagination
          *ngIf="filteredRows.length > 0"
          class="site-card__pagination toolbar-pagination"
          [totalItems]="courseTotal"
          [itemsPerPage]="coursePageSize"
          [currentPage]="coursePage"
          [pageSizeOptions]="pageSizeOptions"
          (pageChanged)="onCoursePageChange($event)"
          (itemsPerPageChange)="onCoursePageSizeChange($event)"
        ></app-pagination>
      </div>

      <div *ngIf="selectedCount > 0" class="site-card__bulkbar">
        <span class="site-card__bulk-count">
          {{ 'pages.tutors.placements.lms.bulk.selected' | translate:{ count: selectedCount } }}
        </span>

        <div *ngIf="selectingFiltered || bulkProgress" class="site-card__bulk-progress">
          <ng-container *ngIf="bulkProgress; else selectingAllCourses">
            <span>
              <ng-container *ngIf="bulkProgressStatus; else bulkProgressCount">
                {{ bulkProgressStatus }}
              </ng-container>
              <ng-template #bulkProgressCount>
                {{ 'pages.tutors.placements.lms.bulk.progress' | translate:{
                  done: bulkProgress.done,
                  total: bulkProgress.total
                } }}
              </ng-template>
            </span>
            <nb-progress-bar
              size="tiny"
              status="primary"
              [value]="bulkProgressPercent"
            ></nb-progress-bar>
          </ng-container>
          <ng-template #selectingAllCourses>
            <nb-icon icon="sync-outline" class="spin"></nb-icon>
            <span>{{ 'pages.tutors.placements.lms.bulk.selecting' | translate }}</span>
          </ng-template>
        </div>

        <div class="site-card__bulk-group site-card__bulk-group--agent">
          <nb-select
            [(ngModel)]="bulkTutorId"
            size="small"
            [placeholder]="'pages.tutors.placements.lms.bulk.agentPlaceholder' | translate"
          >
            <nb-select-label>
              <ng-container *ngIf="tutorById(bulkTutorId) as selectedTutor">
                <span
                  class="tutor-select-label"
                  [title]="tutorFullLabel(selectedTutor)"
                  [nbTooltip]="tutorFullLabel(selectedTutor)"
                  nbTooltipPlacement="top"
                >
                  <span class="tutor-select-label__name">{{ selectedTutor.name }}</span>
                  <span
                    *ngIf="tutorVisualName(selectedTutor)"
                    class="tutor-select-label__visual"
                  >
                    {{ tutorVisualName(selectedTutor) }}
                  </span>
                </span>
              </ng-container>
            </nb-select-label>
            <nb-option *ngFor="let t of tutors" [value]="t.id">
              <span
                class="tutor-option"
                [title]="tutorFullLabel(t)"
                [nbTooltip]="tutorFullLabel(t)"
                nbTooltipPlacement="top"
              >
                <span class="tutor-option__name">{{ t.name }}</span>
                <span
                  *ngIf="tutorVisualName(t)"
                  class="tutor-option__visual"
                >
                  {{ tutorVisualName(t) }}
                </span>
              </span>
            </nb-option>
          </nb-select>
          <button
            nbButton
            size="small"
            status="primary"
            [disabled]="bulkBusy"
            (click)="bulkPlaceSelected()"
          >
            <nb-icon [icon]="bulkPlacing ? 'loader-outline' : 'plus-square-outline'" [class.spin]="bulkPlacing"></nb-icon>
            {{ 'pages.tutors.placements.lms.bulk.place' | translate }}
          </button>
          <button
            type="button"
            class="al-icon-button site-card__bulk-icon"
            [disabled]="selectedPlacedCount === 0 || bulkBusy"
            [nbTooltip]="'pages.tutors.placements.lms.bulk.removeTooltip' | translate"
            nbTooltipPlacement="top"
            [attr.aria-label]="'pages.tutors.placements.lms.bulk.removeTooltip' | translate"
            (click)="bulkRemoveSelected()"
          >
            <nb-icon [icon]="bulkRemoving ? 'loader-outline' : 'trash-2-outline'" [class.spin]="bulkRemoving"></nb-icon>
          </button>
        </div>

        <div class="site-card__bulk-group site-card__bulk-group--run">
          <!-- Kursdaten-Sync and Kurs-Didaktik used to be three buttons that
               looked unrelated, so running both meant guessing the order.
               They are one step now: tick what should happen, press once.
               Didaktik needs synced content, so ticking it ticks Kursdaten
               for any course that has no sync yet. -->
          <span class="site-card__bulk-label">
            {{ 'pages.tutors.placements.lms.bulk.runLabel' | translate }}
          </span>
          <label
            class="site-card__bulk-check"
            [class.is-disabled]="bulkBusy || autoCourseSyncCoversAll"
            [nbTooltip]="'pages.tutors.placements.lms.siteAutoSyncBulkTooltip' | translate"
            [nbTooltipDisabled]="!autoCourseSyncCoversAll"
            nbTooltipPlacement="top"
          >
            <input
              type="checkbox"
              [checked]="bulkRunSync"
              [disabled]="bulkBusy || autoCourseSyncCoversAll"
              (change)="onBulkRunSyncChange($any($event.target).checked)"
            />
            <span>{{ 'pages.tutors.placements.lms.bulk.runSync' | translate }}</span>
          </label>
          <label
            class="site-card__bulk-check"
            [class.is-disabled]="bulkBusy || selectedStructureParseCount === 0"
            [nbTooltip]="'pages.tutors.placements.lms.bulk.structureParseTooltip' | translate"
            nbTooltipPlacement="top"
          >
            <input
              type="checkbox"
              [checked]="bulkRunDidactics"
              [disabled]="bulkBusy || selectedStructureParseCount === 0"
              (change)="onBulkRunDidacticsChange($any($event.target).checked)"
            />
            <span>{{ 'pages.tutors.placements.lms.bulk.runDidactics' | translate }}</span>
          </label>
          <button
            nbButton
            size="small"
            status="primary"
            [disabled]="bulkBusy || !bulkRunAnything"
            (click)="bulkRunSelected()"
          >
            <nb-icon
              [icon]="bulkRunning ? 'loader-outline' : 'play-circle-outline'"
              [class.spin]="bulkRunning"
            ></nb-icon>
            {{ bulkRunActionKey | translate }}
          </button>
          <button
            type="button"
            class="al-icon-button site-card__bulk-icon"
            [disabled]="autoCourseSyncCoversAll || selectedSyncEnabledCount === 0 || bulkBusy"
            [nbTooltip]="(autoCourseSyncCoversAll ? 'pages.tutors.placements.lms.siteAutoSyncBulkTooltip' : 'pages.tutors.placements.lms.bulk.unsyncTooltip') | translate"
            nbTooltipPlacement="top"
            [attr.aria-label]="'pages.tutors.placements.lms.bulk.unsyncTooltip' | translate"
            (click)="bulkUnsyncSelected()"
          >
            <nb-icon [icon]="bulkUnsyncing ? 'loader-outline' : 'slash-outline'" [class.spin]="bulkUnsyncing"></nb-icon>
          </button>
        </div>

        <button
          type="button"
          class="al-icon-button site-card__bulk-icon"
          [disabled]="bulkBusy"
          [attr.aria-label]="'buttons.cancel' | translate"
          (click)="clearSelection()"
        >
          <nb-icon icon="close-outline"></nb-icon>
        </button>
      </div>

      <!-- Empty state — only shown when the channel is healthy and
           genuinely returns zero courses. When degraded, the banner
           above already explains why no data is showing, so we don't
           duplicate the message here. -->
      <div *ngIf="filteredRows.length === 0 && !pluginDegraded" class="site-card__empty">
        <p>{{ 'pages.tutors.placements.lms.noCourses' | translate }}</p>
      </div>

      <!-- Unified matrix: Course · Tutor · Position · KB · Dozent · Actions.
           Scroll container: below the table's min-width the card must NOT
           compress the columns into each other — the table keeps its
           minimum width and scrolls horizontally inside the card. -->
      <div *ngIf="filteredRows.length > 0" class="site-card__table-scroll" appScrollOverflow>
      <table
        class="site-card__table"
      >
        <colgroup>
          <col class="col-select" />
          <col class="col-course" />
          <col class="col-tutor" />
          <col class="col-course-sync" />
          <col class="col-structure-parse" />
          <col class="col-sync-status" />
          <col class="col-kb" />
          <col class="col-dozent" />
          <col class="col-activities" />
          <col class="col-preview" />
          <col class="col-actions" />
        </colgroup>
        <thead>
          <tr>
            <th class="cell-select">
              <nb-checkbox
                [checked]="allVisibleSelected"
                [disabled]="bulkBusy"
                (checkedChange)="toggleFilteredSelection($event)"
              ></nb-checkbox>
            </th>
            <th
              class="is-sortable"
              [nbTooltip]="'pages.tutors.placements.lms.col.courseTooltip' | translate"
              nbTooltipPlacement="top"
            >
              <button
                type="button"
                class="sortable-header"
                (click)="toggleCourseSort()"
                [attr.aria-label]="'pages.tutors.placements.lms.col.courseSort' | translate"
              >
                <span>{{ 'pages.tutors.placements.lms.col.course' | translate }}</span>
                <nb-icon
                  [icon]="courseSortDirection === 'asc' ? 'arrow-upward-outline' : 'arrow-downward-outline'"
                  class="sort-icon"
                ></nb-icon>
              </button>
            </th>
            <th
              [nbTooltip]="'pages.tutors.placements.lms.col.tutorTooltip' | translate"
              nbTooltipPlacement="top"
            >
              {{ 'pages.tutors.placements.lms.col.tutor' | translate }}
            </th>
            <th
              class="cell-course-sync"
              alAnchor="placements.course-sync"
              [nbTooltip]="'pages.tutors.placements.lms.col.courseSyncTooltip' | translate"
              nbTooltipPlacement="top"
            >
              {{ 'pages.tutors.placements.lms.col.courseSync' | translate }}
            </th>
            <th
              class="cell-structure-parse"
              [nbTooltip]="'pages.tutors.placements.lms.col.structureParseTooltip' | translate"
              nbTooltipPlacement="top"
            >
              {{ 'pages.tutors.placements.lms.col.structureParse' | translate }}
            </th>
            <th
              class="cell-sync-status"
              [nbTooltip]="'pages.tutors.placements.lms.col.statusTooltip' | translate"
              nbTooltipPlacement="top"
            >
              {{ 'pages.tutors.placements.lms.col.status' | translate }}
            </th>
            <th
              [nbTooltip]="'pages.tutors.placements.lms.col.kbTooltip' | translate"
              nbTooltipPlacement="top"
            >
              {{ 'pages.tutors.placements.lms.col.kb' | translate }}
            </th>
            <th
              [nbTooltip]="'pages.tutors.placements.lms.col.dozentTooltip' | translate"
              nbTooltipPlacement="top"
            >
              {{ 'pages.tutors.placements.lms.col.dozent' | translate }}
            </th>
            <th
              class="cell-activities"
              [nbTooltip]="'pages.tutors.placements.lms.col.activitiesTooltip' | translate"
              nbTooltipPlacement="top"
            >
              {{ 'pages.tutors.placements.lms.col.activities' | translate }}
            </th>
            <th
              class="cell-preview"
              [nbTooltip]="'pages.tutors.placements.lms.col.previewTooltip' | translate"
              nbTooltipPlacement="top"
            >
              {{ 'pages.tutors.placements.lms.col.preview' | translate }}
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            *ngFor="let row of pagedRows; trackBy: trackRow"
            [class.is-placed]="rowHasAgentPlacement(row)"
            [class.is-selected]="isCourseSelected(row.course)"
            [class.is-saving]="row.saving"
          >
            <td class="cell-select">
              <nb-checkbox
                [checked]="isCourseSelected(row.course)"
                [disabled]="bulkBusy"
                (checkedChange)="toggleCourseSelection(row, $event)"
              ></nb-checkbox>
            </td>
            <!-- Course ─────────────────────────────────────────── -->
            <!-- Einzeilig; bei Überlänge läuft der Name bei Mouseover durch. -->
            <td
              class="cell-course is-openable"
              appMarqueeOnHover
              (click)="openCreate(row.course, agentPlacementForCourse(row.course.id))"
            >
              <div class="site-card__course-name marquee">{{ row.course.fullname }}</div>
              <div class="site-card__course-meta">
                <span>{{ row.course.shortname }}</span>
                <span class="muted">·</span>
                <span class="muted">ID: {{ row.course.id }}</span>
                <ng-container *ngIf="row.course.category_id != null">
                  <span class="muted">·</span>
                  <span class="muted">{{ courseCategoryLabel(row.course) }}</span>
                </ng-container>
              </div>
            </td>

            <!-- Tutor (inline editable) ─────────────────────────── -->
            <td class="cell-tutor">
              <!-- Schmaler Knopf in der Zeile; die Liste öffnet breiter
                   (`optionsWidth`), damit Namen und Anzeigenamen lesbar sind. -->
              <nb-select
                *ngIf="rowHasAgentPlacement(row); else noTutor"
                alAnchor="placements.row-agent"
                [(ngModel)]="row.edit.botId"
                [placeholder]="'pages.tutors.placements.lms.tutorPlaceholder' | translate"
                [optionsWidth]="300"
                fullWidth
                size="small"
              >
                <nb-select-label>
                  <ng-container *ngIf="tutorById(row.edit.botId) as selectedTutor; else unknownTutor">
                    <span class="tutor-select-label">
                      <span
                        class="tutor-select-label__name"
                        appTruncateTooltip
                        [appTruncateTooltip]="tutorFullLabel(selectedTutor)"
                        nbTooltip
                        nbTooltipPlacement="top"
                      >{{ selectedTutor.name }}</span>
                      <span
                        *ngIf="tutorVisualName(selectedTutor)"
                        class="tutor-select-label__visual"
                      >
                        {{ tutorVisualName(selectedTutor) }}
                      </span>
                    </span>
                  </ng-container>
                  <ng-template #unknownTutor>
                    <span class="tutor-select-label">
                      <span
                        class="tutor-select-label__name"
                        appTruncateTooltip
                        [appTruncateTooltip]="tutorName(row.edit.botId)"
                        nbTooltip
                        nbTooltipPlacement="top"
                      >{{ tutorName(row.edit.botId) }}</span>
                    </span>
                  </ng-template>
                </nb-select-label>
                <nb-option *ngFor="let t of tutors" [value]="t.id">
                  <span class="tutor-option">
                    <span
                      class="tutor-option__name"
                      appTruncateTooltip
                      [appTruncateTooltip]="tutorFullLabel(t)"
                      nbTooltip
                      nbTooltipPlacement="top"
                    >{{ t.name }}</span>
                    <span
                      *ngIf="tutorVisualName(t)"
                      class="tutor-option__visual"
                    >
                      {{ tutorVisualName(t) }}
                    </span>
                  </span>
                </nb-option>
              </nb-select>
              <ng-template #noTutor>
                <span class="muted">—</span>
              </ng-template>
            </td>

            <!-- Kursdaten-Sync toggle (replaces the old Position column;
                 Position bleibt im Advanced-Edit-Dialog erreichbar). The
                 actual import is asynchronous on the al-filemanager side;
                 we ack-toast the request and the user sees content arrive
                 in the auto-bucket once the background job completes. -->
            <td class="cell-course-sync">
              <span
                class="course-sync-inline"
                [nbTooltip]="courseSyncToggleTooltipKey(row) | translate"
                [nbTooltipDisabled]="!autoCourseSyncCoversRow(row) && (!row.edit.botId || isCourseSyncFilterCompatible(row.edit.botId))"
                nbTooltipPlacement="top"
              >
                <!-- Keep the toggle in the DOM while saving (spinner as a small
                     overlay) — swapping it out via *ngIf killed the on/off
                     slide animation and made the control "blink". -->
                <span class="course-sync-toggle-slot" alAnchor="placements.row-sync">
                  <nb-toggle
                    [checked]="effectiveCourseSyncEnabled(row)"
                    [disabled]="autoCourseSyncCoversRow(row) || row.savingSync"
                    (checkedChange)="onCourseSyncToggle(row, $event)"
                  ></nb-toggle>
                  <span
                    class="course-sync-spinner course-sync-spinner--overlay"
                    *ngIf="row.savingSync"
                    aria-hidden="true"
                  ></span>
                </span>
                <!-- What the queue is doing for this course. Only shown
                     while there is something to say — an idle lane stays
                     out of the way. -->
                <span
                  *ngIf="jobLane(row, 'content') as lane"
                  class="job-badge"
                  [class.is-running]="lane.state === 'running'"
                  [class.is-done]="lane.state === 'done'"
                  [class.is-failed]="lane.state === 'failed'"
                  [nbTooltip]="jobLaneTooltip(lane)"
                  nbTooltipPlacement="top"
                >
                  <nb-icon
                    [icon]="jobLaneIcon(lane.state)"
                    [class.spin]="lane.state === 'running'"
                  ></nb-icon>
                </span>
              </span>
            </td>

            <!-- Struktur-Analyse (advanced sync) ────────────────── -->
            <td class="cell-structure-parse">
              <span
                class="course-sync-inline"
                [nbTooltip]="structureParseTooltipKey(row) | translate"
                nbTooltipPlacement="top"
              >
                <span class="course-sync-toggle-slot">
                  <nb-toggle
                    [checked]="effectiveStructureParse(row)"
                    [disabled]="!structureParseAvailable(row) || row.savingStructureParse"
                    (checkedChange)="onStructureParseToggle(row, $event)"
                  ></nb-toggle>
                  <span
                    class="course-sync-spinner course-sync-spinner--overlay"
                    *ngIf="row.savingStructureParse"
                    aria-hidden="true"
                  ></span>
                </span>
                <!-- Didactics has follow-up work (unit scripts), so this
                     stays on "läuft" until those are through too. -->
                <span
                  *ngIf="jobLane(row, 'didactics') as lane"
                  class="job-badge"
                  [class.is-running]="lane.state === 'running'"
                  [class.is-done]="lane.state === 'done'"
                  [class.is-failed]="lane.state === 'failed'"
                  [nbTooltip]="jobLaneTooltip(lane)"
                  nbTooltipPlacement="top"
                >
                  <nb-icon
                    [icon]="jobLaneIcon(lane.state)"
                    [class.spin]="lane.state === 'running'"
                  ></nb-icon>
                </span>
              </span>
            </td>

            <!-- Sync status ────────────────────────────────────── -->
            <td class="cell-sync-status">
              <span
                class="sync-status-chip"
                [class.is-active]="courseSyncStatus(row) === 'active'"
                [class.is-disabled]="courseSyncStatus(row) === 'disabled'"
                [class.is-syncing]="courseSyncStatus(row) === 'syncing'"
                [class.is-global]="courseSyncStatus(row) === 'global'"
                [class.is-muted]="courseSyncStatus(row) === 'not_placed'"
                [nbTooltip]="courseSyncStatusTooltipKey(row) | translate"
                nbTooltipPlacement="top"
              >
                <span class="sync-status-chip__dot"></span>
                <span>{{ courseSyncStatusKey(row) | translate }}</span>
              </span>
            </td>

            <!-- KB (inline multi-select; tutor-global) ───────────── -->
            <td class="cell-kb">
              <nb-select
                *ngIf="rowHasAgentPlacement(row); else noKb"
                multiple
                [(ngModel)]="row.edit.kbIds"
                [placeholder]="'pages.tutors.placements.kb.kbPlaceholder' | translate"
                [optionsWidth]="280"
                fullWidth
                size="small"
              >
                <nb-option *ngFor="let ds of dataSources" [value]="ds.id">
                  {{ ds | knowledgeSourceName | async }}
                </nb-option>
              </nb-select>
              <ng-template #noKb>
                <span class="muted">—</span>
              </ng-template>
            </td>

            <!-- Dozent (inline toggle + select; tutor-global) ──────
                 Only enabled when the selected tutor's template
                 supports lecturer linking (alpha_tutor / alpha_flexible).
                 For other templates we grey out the toggle + show a
                 tooltip so the user knows up-front rather than getting
                 a 400 on save. -->
            <td class="cell-dozent">
              <ng-container *ngIf="rowHasAgentPlacement(row); else noDoz">
                <div
                  class="dozent-control has-select"
                >
                  <span
                    class="dozent-control__toggle"
                    [nbTooltip]="
                      isDozentCompatibleTutor(row.edit.botId)
                        ? ''
                        : ('pages.tutors.placements.lms.dozentToggleDisabledHint' | translate)
                    "
                    [nbTooltipDisabled]="isDozentCompatibleTutor(row.edit.botId)"
                    nbTooltipPlacement="top"
                  >
                    <nb-toggle
                      [checked]="row.edit.dozentEnabled"
                      [disabled]="!isDozentCompatibleTutor(row.edit.botId)"
                      (checkedChange)="row.edit.dozentEnabled = $event; !$event && (row.edit.dozentBotId = null)"
                    ></nb-toggle>
                  </span>
                  <nb-select
                    [(ngModel)]="row.edit.dozentBotId"
                    [disabled]="!row.edit.dozentEnabled || !isDozentCompatibleTutor(row.edit.botId)"
                    [placeholder]="'pages.tutors.placements.kb.dozentPlaceholder' | translate"
                    [optionsWidth]="260"
                    fullWidth
                    size="small"
                  >
                    <nb-select-label>
                      <span
                        *ngIf="dozentBotName(row.edit.dozentBotId) as dozentLabel"
                        class="dozent-select-label"
                      >
                        <span
                          class="dozent-select-label__name"
                          appTruncateTooltip
                          nbTooltip
                          nbTooltipPlacement="top"
                        >{{ dozentLabel }}</span>
                      </span>
                    </nb-select-label>
                    <nb-option *ngFor="let d of dozentBots" [value]="d.id">
                      <span class="dozent-option-label">
                        <span
                          class="dozent-option-label__name"
                          appTruncateTooltip
                          nbTooltip
                          nbTooltipPlacement="top"
                        >{{ d.name }}</span>
                      </span>
                    </nb-option>
                  </nb-select>
                </div>
              </ng-container>
              <ng-template #noDoz>
                <span class="muted">—</span>
              </ng-template>
            </td>

            <td class="cell-activities">
              <button
                type="button"
                class="activity-count-pill"
                (click)="openCreate(row.course, agentPlacementForCourse(row.course.id))"
                [nbTooltip]="'pages.tutors.placements.lms.col.placementTargetsTooltip' | translate"
                nbTooltipPlacement="top"
              >
                {{ row.course.relevant_activity_count || 0 }}
              </button>
            </td>

            <td class="cell-preview">
              <button
                *ngIf="rowHasAgentPlacement(row); else noPreview"
                type="button"
                class="al-icon-button al-icon-button--primary site-card__preview-button"
                [class.is-active]="isPreviewActive(row)"
                [nbTooltip]="
                  (isPreviewActive(row)
                    ? 'pages.tutors.placements.lms.rowActions.stopCoursePreview'
                    : 'pages.tutors.placements.lms.rowActions.previewCourseContext'
                  ) | translate
                "
                nbTooltipPlacement="top"
                [attr.aria-label]="
                  (isPreviewActive(row)
                    ? 'pages.tutors.placements.lms.rowActions.stopCoursePreview'
                    : 'pages.tutors.placements.lms.rowActions.previewCourseContext'
                  ) | translate
                "
                (click)="previewPlacementContext(row, $event)"
              >
                <nb-icon [icon]="isPreviewActive(row) ? 'stop-circle-outline' : 'play-circle-outline'"></nb-icon>
              </button>
              <ng-template #noPreview>
                <span class="muted">—</span>
              </ng-template>
            </td>

            <!-- Actions ────────────────────────────────────────── -->
            <td class="cell-actions">
              <div class="site-card__actions-wrap">
                <ng-container *ngIf="rowHasAgentPlacement(row); else placeBtn">
                  <!-- Speichern erscheint nur, wenn die Zeile ungespeicherte
                       Änderungen hat — kein dauerhaft grauer Knopf pro Zeile. -->
                  <button
                    *ngIf="isDirty(row) || row.saving"
                    type="button"
                    nbButton
                    size="small"
                    status="primary"
                    [disabled]="row.saving"
                    alAnchor="placements.row-save"
                    (click)="saveRow(row)"
                  >
                    <nb-icon *ngIf="row.saving" icon="loader-outline" class="spin"></nb-icon>
                    {{ 'pages.tutors.placements.save' | translate }}
                  </button>
                </ng-container>
                <ng-template #placeBtn>
                  <button
                    type="button"
                    nbButton
                    size="small"
                    status="primary"
                    outline
                    class="secondary-action-button"
                    (click)="openCreate(row.course)"
                  >
                    {{ 'pages.tutors.placements.lms.placeAction' | translate }}
                  </button>
                </ng-template>
                <button
                  type="button"
                  class="al-icon-button site-card__menu-button"
                  cdkOverlayOrigin
                  #rowMenuOrigin="cdkOverlayOrigin"
                  [class.is-open]="rowMenuOpen(row)"
                  [attr.aria-label]="'pages.tutors.placements.lms.rowActions.openMenu' | translate"
                  [class.is-busy]="row.refreshingCourse"
                  [attr.aria-busy]="row.refreshingCourse ? 'true' : null"
                  (click)="toggleRowMenu(row, $event)"
                >
                  <!-- „Moodle-Kurs aktualisieren" läuft: das Menü-Symbol
                       dreht sich, bis Metadaten und Inhalts-Sync angestoßen
                       sind (Regel 19, Stufe D). -->
                  <nb-icon
                    [icon]="row.refreshingCourse ? 'sync-outline' : 'more-vertical-outline'"
                    [class.al-spin]="row.refreshingCourse"
                  ></nb-icon>
                </button>
                <!-- Im CDK-Overlay, nicht in der Zelle: der waagerechte
                     Scrollrahmen der Tabelle schnitt das Menü sonst ab — bei
                     nur einer Zeile blieb davon ein Eintrag übrig (siehe
                     rowMenuPositions). -->
                <ng-template
                  cdkConnectedOverlay
                  #rowMenuOverlay="cdkConnectedOverlay"
                  [cdkConnectedOverlayOrigin]="rowMenuOrigin"
                  [cdkConnectedOverlayOpen]="rowMenuOpen(row)"
                  [cdkConnectedOverlayPositions]="rowMenuPositions"
                  (attach)="onRowMenuAttached(rowMenuOverlay)"
                  (detach)="onRowMenuDetached(row, rowMenuOrigin)"
                >
                  <div
                    class="site-card-row-menu-popover"
                    (click)="$event.stopPropagation()"
                  >
                    <button
                      *ngFor="let menuItem of visibleRowActionItems(row); trackBy: trackRowMenuItem"
                      type="button"
                      class="site-card-row-menu-popover__item"
                      [class.is-danger]="menuItem.key === 'removePlacement'"
                      (click)="onPlacementRowAction(row, menuItem, $event)"
                    >
                      <nb-icon
                        [icon]="rowActionIcon(menuItem)"
                        [pack]="rowActionIconPack(menuItem)"
                      ></nb-icon>
                      <span>{{ menuItem.title }}</span>
                    </button>
                  </div>
                </ng-template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </ng-container>
</section>
